import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { devWarn } from "@/lib/logger";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        if (active) setUser(data);
      } catch (err) {
        devWarn("Auth check failed", err?.response?.status);
        if (active) setUser(null);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data);
    return data;
  }, []);

  const registerStart = useCallback(async (payload) => {
    // Step 1 — request a verification OTP. Does NOT create the user.
    const { data } = await api.post("/auth/register-start", payload);
    return data;
  }, []);

  const registerVerify = useCallback(async (email, otp) => {
    // Step 2 — consume OTP. On success the backend sets the auth cookie and
    // returns the freshly-created user doc, which we mirror into auth state.
    const { data } = await api.post("/auth/register-verify", { email, otp });
    setUser(data);
    return data;
  }, []);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch (err) {
      devWarn("Logout request failed", err);
    }
    // Only non-secret UX preferences live in localStorage now (e.g. active
    // company id). The session is in an httpOnly cookie that the server clears.
    localStorage.removeItem("tx_company_id");
    setUser(null);
  }, []);

  // Force a re-fetch of /auth/me. Used after profile updates so the sidebar
  // / topbar pick up name + email changes without a full reload.
  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch (err) {
      devWarn("Auth refresh failed", err);
      return null;
    }
  }, []);

  const value = useMemo(
    () => ({ user, loading, login, registerStart, registerVerify, logout, refresh }),
    [user, loading, login, registerStart, registerVerify, logout, refresh]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
